'use strict';

var React = require('react');
var styled = require('styled-components');
var component = require('../Icon/component.js');
require('../Tooltip/component.js');
var index = require('../Flex/index.js');

function _interopDefaultCompat (e) { return e && typeof e === 'object' && 'default' in e ? e : { default: e }; }

var React__default = /*#__PURE__*/_interopDefaultCompat(React);
var styled__default = /*#__PURE__*/_interopDefaultCompat(styled);

const outlineChipVariants = {
    ['default-outline']: 'blue-outline',
    ['blue-outline']: 'blue-outline',
    ['green-outline']: 'green-outline',
    ['grey-outline']: 'grey-outline',
    ['orange-outline']: 'orange-outline',
    ['purple-outline']: 'purple-outline',
    ['red-outline']: 'red-outline',
    ['yellow-outline']: 'yellow-outline',
};
const chipVariants = {
    default: 'blue',
    blue: 'blue',
    green: 'green',
    grey: 'grey',
    orange: 'orange',
    purple: 'purple',
    red: 'red',
    yellow: 'yellow',
    ...outlineChipVariants,
};
const textColorMapping = (theme) => ({
    [chipVariants.blue]: theme.designTokens.chipDefaultTextColor,
    [chipVariants['blue-outline']]: theme.designTokens.chipDefaultOutlineTextColor,
    [chipVariants.green]: theme.designTokens.chipGreenTextColor,
    [chipVariants['green-outline']]: theme.designTokens.chipDefaultTextColor,
    [chipVariants.grey]: theme.designTokens.chipGreyTextColor,
    [chipVariants['grey-outline']]: theme.designTokens.chipDefaultTextColor,
    [chipVariants.orange]: theme.designTokens.chipOrangeTextColor,
    [chipVariants['orange-outline']]: theme.designTokens.chipDefaultTextColor,
    [chipVariants.purple]: theme.designTokens.chipPurpleTextColor,
    [chipVariants['purple-outline']]: theme.designTokens.chipDefaultTextColor,
    [chipVariants.red]: theme.designTokens.chipRedTextColor,
    [chipVariants['red-outline']]: theme.designTokens.chipDefaultTextColor,
    [chipVariants.yellow]: theme.designTokens.chipYellowTextColor,
    [chipVariants['yellow-outline']]: theme.designTokens.chipDefaultTextColor,
    disabled: theme.designTokens.chipDisabledTextColor,
});
const backgroundColorMapping = (theme) => ({
    [chipVariants.blue]: theme.designTokens.chipBlueBackgroundColor,
    [chipVariants['blue-outline']]: theme.designTokens.chipBlueOutlineBackgroundColor,
    [chipVariants.green]: theme.designTokens.chipGreenBackgroundColor,
    [chipVariants['green-outline']]: theme.designTokens.chipGreenOutlineBackgroundColor,
    [chipVariants.grey]: theme.designTokens.chipGreyBackgroundColor,
    [chipVariants['grey-outline']]: theme.designTokens.chipGreyOutlineBackgroundColor,
    [chipVariants.orange]: theme.designTokens.chipOrangeBackgroundColor,
    [chipVariants['orange-outline']]: theme.designTokens.chipOrangeOutlineBackgroundColor,
    [chipVariants.purple]: theme.designTokens.chipPurpleBackgroundColor,
    [chipVariants['purple-outline']]: theme.designTokens.chipPurpleOutlineBackgroundColor,
    [chipVariants.red]: theme.designTokens.chipRedBackgroundColor,
    [chipVariants['red-outline']]: theme.designTokens.chipRedOutlineBackgroundColor,
    [chipVariants.yellow]: theme.designTokens.chipYellowBackgroundColor,
    [chipVariants['yellow-outline']]: theme.designTokens.chipYellowOutlineBackgroundColor,
    removable: theme.designTokens.chipRemovableBackgroundColor,
    disabled: theme.designTokens.chipDisabledBackgroundColor,
    ['disabled-outline']: theme.designTokens.chipDisabledBackgroundColor,
});
const borderColorMapping = (theme) => ({
    [chipVariants['blue-outline']]: theme.designTokens.chipBlueOutlineBorderColor,
    [chipVariants['green-outline']]: theme.designTokens.chipGreenOutlineBorderColor,
    [chipVariants['grey-outline']]: theme.designTokens.chipGreyOutlineBorderColor,
    [chipVariants['orange-outline']]: theme.designTokens.chipOrangeOutlineBorderColor,
    [chipVariants['purple-outline']]: theme.designTokens.chipPurpleOutlineBorderColor,
    [chipVariants['red-outline']]: theme.designTokens.chipRedOutlineBorderColor,
    [chipVariants['yellow-outline']]: theme.designTokens.chipYellowOutlineBorderColor,
    ['disabled-outline']: theme.designTokens.chipDisabledBackgroundColor,
});
const getTextColor = ({ theme, disabled, variant = chipVariants.default, }) => disabled ? textColorMapping(theme).disabled : textColorMapping(theme)[chipVariants[variant]];
const getBackgroundColor = ({ theme, disabled, variant = chipVariants.default, }) => disabled ? backgroundColorMapping(theme).disabled : backgroundColorMapping(theme)[chipVariants[variant]];
const getBorderColor = ({ theme, disabled, variant = chipVariants.default, }) => {
    const isOutlineVariant = variant.includes('outline');
    if (!isOutlineVariant) {
        return 'none';
    }
    const color = disabled
        ? borderColorMapping(theme)['disabled-outline']
        : borderColorMapping(theme)[outlineChipVariants[variant]];
    return `1px solid ${color}`;
};
const getTextAlign = ({ textAlign }) => textAlign;
const StylizedCustomIcon = styled__default.default(component.Icon) `
  font-size: 14px;
`;
const RemoveIcon = styled__default.default(StylizedCustomIcon) `
  &:hover {
    cursor: ${({ disabled }) => (disabled ? 'inherit' : 'pointer')};
  }
`;
const GenericChip = React.forwardRef(({ disabled, icon, label, textAlign, onRemove, iconPosition = 'left', removeIconPosition = 'right', ...props }, ref) => {
    const removeIcon = onRemove && (React__default.default.createElement(RemoveIcon, { disabled: disabled, name: "clear-circle-filled", onClick: disabled ? undefined : onRemove }));
    const customIcon = icon && React__default.default.createElement(StylizedCustomIcon, { name: icon });
    return (React__default.default.createElement("div", { ref: ref, ...props },
        React__default.default.createElement(index.Flex.Row, { space: "xs", flexWrap: "nowrap" },
            removeIconPosition === 'left' && removeIcon,
            React__default.default.createElement(index.Flex.Row, { space: "xs" },
                iconPosition === 'left' && customIcon,
                label && React__default.default.createElement("span", null, label),
                iconPosition === 'right' && customIcon),
            removeIconPosition === 'right' && removeIcon)));
});
const Chip = styled__default.default(GenericChip) `
  align-items: center;
  display: inline-flex;
  justify-content: center;
  position: relative;

  min-height: 1.5rem;
  min-width: 1.5rem;
  margin: 0;
  padding: 0 0.5rem;

  background-color: ${getBackgroundColor};

  border: ${getBorderColor};
  border-radius: ${({ theme }) => theme.designTokens.chipRadius};

  color: ${getTextColor};
  font: inherit;
  font-family: ${({ theme }) => theme.designTokens.chipFontFamily};
  font-size: ${({ theme }) => theme.designTokens.chipFontSize};
  font-weight: ${({ theme }) => theme.designTokens.chipFontWeight};
  line-height: ${({ theme }) => theme.designTokens.chipFontLineHeight};
  letter-spacing: 0;
  text-align: ${getTextAlign};
`;
Chip.defaultProps = {
    disabled: false,
    variant: chipVariants.default,
    textAlign: 'center',
};

exports.Chip = Chip;
exports.GenericChip = GenericChip;
exports.backgroundColorMapping = backgroundColorMapping;
exports.borderColorMapping = borderColorMapping;
exports.chipVariants = chipVariants;
exports.getBackgroundColor = getBackgroundColor;
exports.getBorderColor = getBorderColor;
exports.getTextColor = getTextColor;
exports.textColorMapping = textColorMapping;
//# sourceMappingURL=component.js.map
